import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Alert className="mb-6 border-[#E5B33E] bg-[#E5B33E]/10">
          <AlertTriangle className="h-5 w-5 text-[#E5B33E]" />
          <AlertDescription className="text-foreground ml-2">
            <strong>Important:</strong> Please read this disclaimer carefully before using MudraFX.
          </AlertDescription>
        </Alert>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#E5B33E]" style={{ fontFamily: 'Manrope' }}>
              MudraFX: Accuracy & Financial Disclaimer
            </CardTitle>
            <p className="text-sm text-muted-foreground">Effective Date: January 28, 2025</p>
          </CardHeader>
          <CardContent className="prose prose-invert max-w-none space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg border border-border">
              <p className="text-muted-foreground italic">
                The information provided by MudraFX is for general informational purposes only. All currency conversion rates and financial data are provided "as is" and are subject to change without notice.
              </p>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">1.</span> Data Accuracy
              </h2>
              <p className="text-muted-foreground">
                While we strive to provide the most current and accurate exchange rates, data is sourced from third-party providers and may be delayed. MudraFX does not guarantee the accuracy, adequacy, validity, reliability, or completeness of any information on the site or mobile app.
              </p>
              <div className="mt-3 pl-4 border-l-2 border-primary">
                <p className="text-sm text-muted-foreground italic">
                  Exchange rates are obtained from ExchangeRate-API and Frankfurter API. Historical data may not reflect real-time market conditions.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">2.</span> Not Financial Advice
              </h2>
              <p className="text-muted-foreground">
                MudraFX is not a financial institution, bank, or currency brokerage. The conversions provided are estimates and should not be used as the sole basis for making financial decisions, trades, or international transfers.
              </p>
              <div className="mt-3 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm font-medium text-foreground mb-2">⚠️ Important Notice:</p>
                <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                  <li>MudraFX does not provide investment, tax, legal, or accounting advice</li>
                  <li>Always consult with qualified financial professionals before making decisions</li>
                  <li>Verify exchange rates with your bank or financial institution before transactions</li>
                  <li>Rates displayed may differ from actual transaction rates offered by banks</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">3.</span> Limitation of Liability
              </h2>
              <p className="text-muted-foreground">
                Under no circumstance shall MudraFX or its developers be held liable for any loss or damage of any kind incurred as a result of the use of the app or reliance on any information provided. Your use of the app and your reliance on any information is solely at your own risk.
              </p>
              <div className="mt-3">
                <p className="text-sm font-medium text-foreground mb-2">This includes but is not limited to:</p>
                <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                  <li>Financial losses from currency exchange transactions</li>
                  <li>Losses from investment decisions based on our data</li>
                  <li>Damages from service interruptions or data inaccuracies</li>
                  <li>Losses from third-party API failures or delays</li>
                  <li>Any consequential, incidental, or indirect damages</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">4.</span> Third-Party Data Sources
              </h2>
              <p className="text-muted-foreground mb-3">
                MudraFX relies on the following third-party services for exchange rate data:
              </p>
              <div className="space-y-2">
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="text-sm font-medium text-foreground">ExchangeRate-API</p>
                  <p className="text-xs text-muted-foreground mt-1">Provides real-time currency conversion rates</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="text-sm font-medium text-foreground">Frankfurter API</p>
                  <p className="text-xs text-muted-foreground mt-1">Provides historical exchange rate data (1999-present)</p>
                </div>
              </div>
              <p className="text-muted-foreground mt-3 text-sm italic">
                We are not responsible for the accuracy, availability, or reliability of these third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">5.</span> Use at Your Own Risk
              </h2>
              <p className="text-muted-foreground">
                By using MudraFX, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-2 mt-2">
                <li>You understand the limitations and disclaimers outlined above</li>
                <li>You will not hold MudraFX liable for any financial decisions made using our data</li>
                <li>You will verify all exchange rates independently before conducting transactions</li>
                <li>You accept full responsibility for any consequences of using this service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="text-[#E5B33E]">6.</span> Updates and Modifications
              </h2>
              <p className="text-muted-foreground">
                This disclaimer may be updated from time to time without prior notice. Continued use of MudraFX after any modifications constitutes acceptance of the updated disclaimer.
              </p>
            </section>

            <section className="border-t border-border pt-6">
              <h2 className="text-xl font-semibold text-foreground mb-3">Contact Information</h2>
              <p className="text-muted-foreground mb-2">
                If you have any questions or concerns about this disclaimer, please contact us:
              </p>
              <p className="text-primary">
                Email: <a href="mailto:support@mudrafx.app" className="hover:underline font-medium">support@mudrafx.app</a>
              </p>
            </section>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-6">
              <p className="text-sm text-foreground font-medium">
                By using MudraFX, you acknowledge that you have read, understood, and agree to be bound by this disclaimer.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}